import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import type { PreVisitAnswer, PreVisitPhoto, PreVisitQuestion } from '@/api/preVisit/preVisitApi.types';
import { Icon } from '@/components/common/Icon';
import { PRE_VISIT_QUESTION_TYPE } from '@/constants/preVisitQuestionTypes';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { resolveLocalizedText } from '@/utils/localizedText';

function hasAnswerContent(answer: PreVisitAnswer): boolean {
  if (answer.photos.length > 0) {
    return true;
  }
  if (answer.value === null) {
    return false;
  }
  if (Array.isArray(answer.value)) {
    return answer.value.length > 0;
  }
  return answer.value.trim().length > 0;
}

function resolveOptionLabel(
  optionCode: string,
  question: PreVisitQuestion | undefined,
  activeLanguage: string
): string {
  if (question === undefined) {
    return optionCode;
  }
  const matchingOption = question.options.find(function matches(option) {
    return option.code === optionCode;
  });
  if (matchingOption === undefined) {
    return optionCode;
  }
  return resolveLocalizedText(matchingOption.labelEn, matchingOption.labelPl, activeLanguage);
}

function isPreviewableContentType(contentType: string): boolean {
  return contentType !== 'image/heic' && contentType !== 'image/heif';
}

interface PreVisitAnswersListProps {
  answers: PreVisitAnswer[];
  questionsByCode?: Record<string, PreVisitQuestion>;
  onPreviewPhoto?: (photo: PreVisitPhoto) => void;
}

export function PreVisitAnswersList({
  answers,
  questionsByCode,
  onPreviewPhoto,
}: PreVisitAnswersListProps) {
  const { t, i18n } = useTranslation('intake');
  const answersWithContent = answers.filter(hasAnswerContent);

  if (answersWithContent.length === 0) {
    return <p className="text-sm text-zoomuted">{t('preVisit.answersList.empty')}</p>;
  }

  return (
    <dl className="space-y-3">
      {answersWithContent.map(function renderAnswer(answer) {
        const question = questionsByCode === undefined ? undefined : questionsByCode[answer.questionCode];
        return (
          <div key={answer.questionCode}>
            <dt className="text-[11px] font-semibold text-zoomuted">{answer.label}</dt>
            <dd className="text-sm text-zooindigo">
              <PreVisitAnswerValue
                answer={answer}
                question={question}
                activeLanguage={i18n.language}
                onPreviewPhoto={onPreviewPhoto}
              />
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

interface PreVisitAnswerValueProps {
  answer: PreVisitAnswer;
  question: PreVisitQuestion | undefined;
  activeLanguage: string;
  onPreviewPhoto?: (photo: PreVisitPhoto) => void;
}

function PreVisitAnswerValue({
  answer,
  question,
  activeLanguage,
  onPreviewPhoto,
}: PreVisitAnswerValueProps) {
  const { t } = useTranslation('intake');

  if (answer.questionType === PRE_VISIT_QUESTION_TYPE.photos) {
    return (
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-1">
        {answer.photos.map(function renderPhoto(photo) {
          return (
            <button
              key={photo.id}
              type="button"
              disabled={onPreviewPhoto === undefined}
              onClick={function preview() {
                if (onPreviewPhoto !== undefined) {
                  onPreviewPhoto(photo);
                }
              }}
              className="aspect-square rounded-lg overflow-hidden border border-zooborder bg-zoooffwhite flex items-center justify-center"
            >
              {isPreviewableContentType(photo.contentType) ? (
                <img src={photo.url} alt={photo.filename} className="w-full h-full object-cover" />
              ) : (
                <Icon name="FileText" className="text-zoomuted" />
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (answer.value === null) {
    return <>—</>;
  }

  if (answer.questionType === PRE_VISIT_QUESTION_TYPE.yesNo) {
    return <>{answer.value === 'true' ? t('preVisit.yes') : t('preVisit.no')}</>;
  }

  if (answer.questionType === PRE_VISIT_QUESTION_TYPE.singleChoice && typeof answer.value === 'string') {
    return <>{resolveOptionLabel(answer.value, question, activeLanguage)}</>;
  }

  if (answer.questionType === PRE_VISIT_QUESTION_TYPE.multipleChoice && Array.isArray(answer.value)) {
    const labels = answer.value.map(function toLabel(optionCode) {
      return resolveOptionLabel(optionCode, question, activeLanguage);
    });
    return <>{labels.join(', ')}</>;
  }

  if (answer.questionType === PRE_VISIT_QUESTION_TYPE.date && typeof answer.value === 'string') {
    return <>{formatDateToDayMonthYear(parseISO(answer.value))}</>;
  }

  return <span className="whitespace-pre-wrap">{typeof answer.value === 'string' ? answer.value : ''}</span>;
}
